import { Card } from "@mui/material";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import { useState } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Form from "../components/form/Form";
import FormButton from "../components/form/FormButton";
import { FormProvider } from "../components/form/FormProvider";
import FormTextField from "../components/form/FormTextField";
import FormMultiSelectChips from "../components/form/FormMultiSelectChips";
import FormConditionCheck from "../components/form/FormConditionCheck";
import { useMessage } from "../context/MessageProvider";
import { getAssessment } from "../models/assessments";
import { listElephants } from "../models/elephants";
import { getFacility } from "../models/facility";
import FormRadioGroup from "../components/form/FormRadioGroup";
import {
  newSectionC,
  listSectionCsByAssessment,
  getSectionC,
  saveSectionC,
} from "../models/sectionc";
import { today } from "../utils/datetime";
import { ArrowBack } from "@mui/icons-material";

import { useConnectivity } from "../context/ConnectivityProvider";

import CustomTooltip from '../components/tooltip/CustomTooltip';
import HelpIcon from '@mui/icons-material/Help';


export async function loader({ params }) {
  const assessment = await getAssessment(params.assessmentId);
  const sectionC = await getSectionC(params.sectionCId);
  const sectionCs = await listSectionCsByAssessment(assessment.id);

  return {
    assessment,
    elephants: await listElephants(),
    facility: await getFacility(),
    sectionC,
    sectionCs,
  };
}

export default function AssessmentSectionCFormPage({ title = "EWP", ...props }) {

  const { setSuccess, setError } = useMessage();
  const navigate = useNavigate();
  const { assessment, sectionC, sectionCs, elephants } = useLoaderData();

  const { isOnline } = useConnectivity();
  const [working,setWorking] = useState(false);

  const behaviors={
    "feeding": { 
      label: "Feeding", 
      tooltip: <CustomTooltip keyString="section_c_feeding"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: false,
      hasElephants: false,
    },

    "lying_down_alone": {
      label: "Lying Down Alone",
      tooltip: <CustomTooltip keyString="section_c_lying_down_alone"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: false,
      hasElephants: false,
    },

    "lying_down_with_others": {
      label: "Lying Down With Others",
      tooltip: <CustomTooltip keyString="section_c_lying_down_with_others"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: false,
      hasElephants: true,
      elephantsKey: "lying_down_with_others_elephants",
    },

    "stereotyping": { 
      label: "Stereotyping", 
      tooltip: <CustomTooltip keyString="section_c_stereotyping"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: true,
      hasElephants: false,
      descriptionKey: "stereotyping_description"
    },

    "self_maintenance": {
      label: "Self Maintenance",
      tooltip: <CustomTooltip keyString="section_c_self_maintenance"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: true,
      hasElephants: false,
      descriptionKey: "self_maintenance_description"
    },

    "interaction_with_environment": {
      label: "Interaction with Environment",
      tooltip: <CustomTooltip keyString="section_c_interaction_with_environment"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: true,
      hasElephants: false,
      descriptionKey: "interaction_with_environment_description"
    },

    "social_affiliative": {
      label: "Social Affiliative",
      tooltip: <CustomTooltip keyString="section_c_social_affiliative"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: false,
      hasElephants: true,
      elephantsKey: "social_affiliative_elephants",
    },

    "other": { 
      label: "Other", 
      tooltip: <CustomTooltip keyString="section_c_other"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: true,
      hasElephants: false,
      descriptionKey: "other_description"
    },
    "out_of_view": { 
      label: "Out of View", 
      tooltip: <CustomTooltip keyString="section_c_out_of_view"><HelpIcon sx={{marginLeft: 1}}/></CustomTooltip>,
      hasDescription: false,
      hasElephants: false,
    },
  };


  const behaviorList = Object.keys(behaviors).map((key)=>{
    const record = behaviors[key];
    return {
      label: record.label,
      value: key,
      tooltip: record.tooltip || undefined,
    }
  });

  const descriptionCheckValues = Object.keys((behaviors)).reduce((acc,cur)=>{
    const record = behaviors[cur];
    if (record.hasDescription) acc.push(cur);
    return acc;
  },[]);

  const associatedElephantCheckValues = Object.keys((behaviors)).reduce((acc,cur)=>{
    const record = behaviors[cur];
    if (record.hasElephants) acc.push(cur);
    return acc;
  },[]);


  const elephantList = elephants.map((cur)=>{
    return {
      label: cur.name,
      value: cur.id,
    };
  });

  const onSubmit = async(formData) => {
    try {

      setWorking(true);
      const payload = {...formData, submitted_on: today()};
      if (behaviors[payload.behavior]?.hasDescription === false) payload.description = null;
      if (behaviors[payload.behavior]?.hasElephants === false) payload.associated_elephants = [];
      console.log("payload", payload);

      const saveResult = await saveSectionC(payload,isOnline);
      console.log('save saveResult', saveResult);


      // determine which record is next and navigate there
      // after saving the current record, find the next observation window and switch to it
      // we want to find the next without a submission ID and that isn't the current record

      const recordsNotStarted = sectionCs.filter((cur)=>{
        return cur.id !== sectionC.id && cur.submitted_on === null;
      });

      if (recordsNotStarted.length > 0) {

        const record = recordsNotStarted[0]; // get the next record from the filter list, since they're in time series order
        const payload = {...record,observed_on: today()};
        console.log("section c payload: ", payload);
        const nextSaveResult = await saveSectionC(payload,isOnline);
        console.log("next sectionc save: ", nextSaveResult);

        setSuccess("Started Section C " + record.observation_range);
        navigate("/assessments/" + assessment.id + "/sectionc/" + record.id);

      } else {
        navigate("/assessments/" + assessment.id);
      }


    } catch (error) {
      console.error(error);
      setError(error.message);
    } finally {
      setWorking(false);
    }
  };

  const defaultValues = {...newSectionC()};

  return (
    <Container component="main" maxWidth="sm">
      <ArrowBack
        onClick={() => navigate(`/assessments/${assessment.id}`)}
        sx={{ position: "absolute", "&:hover": { cursor: "pointer" } }}
      />
      <Header
        label={`${title} ${
          sectionC ? "(" + sectionC?.observation_range + ")" : ""
        }`}
      />
        <FormProvider defaultValues={defaultValues} useFormConfig={{values: {...sectionC}}} onSubmit={onSubmit}>
          <Form>
                <Card sx={{ padding: 2 }}>

                  <Grid container spacing={2}>
                    <Grid xs={12}>
                      <FormRadioGroup
                        name="behavior"
                        label="Behavior"
                        list={behaviorList}
                      />
                    </Grid>

                    <FormConditionCheck name="behavior" value={associatedElephantCheckValues}>
                      <Grid xs={12}>
                        <FormMultiSelectChips enableSelectAll name="associated_elephants" label="Associated Elephants" list={elephantList} />
                      </Grid>
                    </FormConditionCheck>

                    <FormConditionCheck name="behavior" value={descriptionCheckValues}>
                      <Grid xs={12}>
                        <FormTextField
                          name="description"
                          multiline
                          rows={4}
                          sx={{mb:2}}
                          label="Description"
                        />
                      </Grid>
                    </FormConditionCheck>

                    <Grid xs={12}>
                      <FormTextField
                        name="additional_comments"
                        label="Additional Comments"
                        sx={{mt:2}}
                      />
                    </Grid>

                    <Grid xs={12}>
                      <FormButton working={working}>Next</FormButton>
                    </Grid>


                  </Grid>
                </Card>


          </Form>
        </FormProvider>
    </Container>
  );
}